// Single source of truth for SimCheck's legal documents and their versions.
//
// CURRENT_LEGAL_VERSION / ORG_LEGAL_VERSION stamp the version a user/org
// accepted (recorded on users/{uid}.legal and organizations/{orgId}.legal).
// Bump them when a document materially changes so the acceptance record reflects
// the version in force at accept time.
//
// IMPORTANT: bumping the version does NOT re-prompt existing users. The
// click-through gate only blocks first-ever acceptance; updated documents are
// communicated by notice/email and continued use constitutes acceptance (see
// each document's "Changes" section). If a future material change ever requires
// a hard re-acceptance, gate on a dedicated constant rather than this one.
//
// Versions are date strings (YYYY-MM-DD) for human readability.

export const CURRENT_LEGAL_VERSION = '2026-05-31';
export const ORG_LEGAL_VERSION = '2026-05-31';

// Hard re-acceptance gate (issue #99, AC #5).
//
// The default model is "continued use = acceptance": a version bump does NOT
// re-prompt existing users. Set REQUIRE_REACCEPT_VERSION to a CURRENT_LEGAL_VERSION
// value (e.g. '2026-05-31') ONLY when a material change has been signed off by the
// product/legal owner and existing users must actively re-accept on next login.
// When non-null, any user whose recorded eulaVersion predates it is re-gated.
// Leaving it null preserves today's behavior — DO NOT set it without that sign-off.
export const REQUIRE_REACCEPT_VERSION: string | null = null;

export type LegalDocKey = 'terms' | 'privacy' | 'eula' | 'acceptable-use';

export interface LegalDocMeta {
  key: LegalDocKey;
  title: string;
  /** Public route — reachable without authentication. */
  route: string;
  /** Per-document version, recorded in the acceptance audit trail. */
  version: string;
  /** True for the Terms of Service / MSA, which also binds the organization. */
  orgBinding?: boolean;
}

export const LEGAL_DOCS: LegalDocMeta[] = [
  {
    key: 'eula',
    title: 'End User License Agreement',
    route: '/legal/eula',
    version: CURRENT_LEGAL_VERSION,
  },
  {
    key: 'terms',
    title: 'Terms of Service',
    route: '/legal/terms',
    version: ORG_LEGAL_VERSION,
    orgBinding: true,
  },
  {
    key: 'acceptable-use',
    title: 'Acceptable Use Policy',
    route: '/legal/acceptable-use',
    version: CURRENT_LEGAL_VERSION,
  },
  {
    key: 'privacy',
    title: 'Privacy Policy',
    route: '/legal/privacy',
    version: CURRENT_LEGAL_VERSION,
  },
];

export function getLegalDoc(key: LegalDocKey): LegalDocMeta | undefined {
  return LEGAL_DOCS.find((d) => d.key === key);
}

/** Map of { docKey: version } recorded on the user doc at acceptance time. */
export function acceptedDocsSnapshot(): Record<string, string> {
  return Object.fromEntries(LEGAL_DOCS.map((d) => [d.key, d.version]));
}

/**
 * Whether a recorded acceptance is still current for the gate (#99 AC #5).
 *
 * - If REQUIRE_REACCEPT_VERSION is null (default), ANY prior acceptance counts as
 *   current — continued use is acceptance; a version bump does not re-prompt.
 * - If REQUIRE_REACCEPT_VERSION is set, an acceptance is current only when its
 *   RECORDED version is >= the required version (date strings compare
 *   lexicographically, which is also chronological for YYYY-MM-DD).
 *
 * `acceptedVersion` is null/undefined when no version was recorded.
 * `hasUnversionedAcceptance` covers legacy records that carry `acceptedAt` but
 * no `eulaVersion`: they satisfy the default "accepted at least once" gate, but
 * are deliberately RE-GATED when REQUIRE_REACCEPT_VERSION is set — we can't
 * prove which version of the text they accepted, so coercing them to "current"
 * would defeat the re-acceptance gate.
 */
export function legalIsCurrent(
  acceptedVersion: string | null | undefined,
  hasUnversionedAcceptance = false
): boolean {
  if (!REQUIRE_REACCEPT_VERSION) return !!acceptedVersion || hasUnversionedAcceptance;
  if (!acceptedVersion) return false;
  return acceptedVersion >= REQUIRE_REACCEPT_VERSION;
}
